import { useRef } from 'react';
import { Editor } from '@tiptap/react';
import styles from './EditorToolbar.module.css';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaListUl,
  FaListOl,
  FaUndo,
  FaRedo,
  FaImage,
  FaHighlighter,
} from 'react-icons/fa';
import { fileToDataUrl, isAcceptableImage } from '../utils/image-upload';

interface EditorToolbarProps {
  editor: Editor | null;
}

interface ToolbarItem {
  icon: React.ReactNode;
  title: string;
  action: () => boolean;
  isActive?: boolean;
  disabled?: boolean;
}

const FONT_SIZES = ['8', '9', '10', '11', '12', '14', '16', '18', '20', '24', '28', '32', '36', '48', '72'];
const FONT_FAMILIES = [
  { label: 'Calibri', value: 'Calibri' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Courier New', value: 'Courier New' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Verdana', value: 'Verdana' },
  { label: 'Tahoma', value: 'Tahoma' },
];

export function EditorToolbar({ editor }: EditorToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const colorInputRef = useRef<HTMLInputElement | null>(null);
  const highlightInputRef = useRef<HTMLInputElement | null>(null);

  if (!editor) return null;

  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !isAcceptableImage(file)) return;
    try {
      const src = await fileToDataUrl(file);
      editor.chain().focus().setImage({ src }).run();
    } catch (err) {
      console.warn('Falha ao inserir imagem:', err);
    }
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const size = e.target.value;
    if (size) {
      editor.chain().focus().setFontSize(`${size}pt`).run();
    }
  };

  const handleFontFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const font = e.target.value;
    if (font) {
      editor.chain().focus().setFontFamily(font).run();
    } else {
      editor.chain().focus().unsetFontFamily().run();
    }
  };

  const handleHeadingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const level = Number(e.target.value);
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 | 4 }).run();
    }
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    editor.chain().focus().setColor(e.target.value).run();
  };

  const handleHighlightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    editor.chain().focus().toggleHighlight({ color: e.target.value }).run();
  };

  const getCurrentHeading = () => {
    for (let i = 1; i <= 4; i++) {
      if (editor.isActive('heading', { level: i })) return String(i);
    }
    return '0';
  };

  const getCurrentFontFamily = () => {
    const attrs = editor.getAttributes('textStyle');
    return attrs?.fontFamily || 'Calibri';
  };

  const getCurrentFontSize = () => {
    const attrs = editor.getAttributes('textStyle');
    const raw = attrs?.fontSize as string | undefined;
    if (!raw) return '12';
    return raw.replace(/pt$/i, '');
  };

  const groups: { items: ToolbarItem[] }[] = [
    {
      items: [
        {
          icon: <FaUndo />,
          title: 'Desfazer',
          action: () => editor.chain().focus().undo().run(),
          disabled: !editor.can().undo(),
        },
        {
          icon: <FaRedo />,
          title: 'Refazer',
          action: () => editor.chain().focus().redo().run(),
          disabled: !editor.can().redo(),
        },
      ],
    },
    {
      items: [
        {
          icon: <FaBold />,
          title: 'Negrito',
          action: () => editor.chain().focus().toggleBold().run(),
          isActive: editor.isActive('bold'),
        },
        {
          icon: <FaItalic />,
          title: 'Itálico',
          action: () => editor.chain().focus().toggleItalic().run(),
          isActive: editor.isActive('italic'),
        },
        {
          icon: <FaUnderline />,
          title: 'Sublinhado',
          action: () => editor.chain().focus().toggleUnderline().run(),
          isActive: editor.isActive('underline'),
        },
        {
          icon: <FaStrikethrough />,
          title: 'Tachado',
          action: () => editor.chain().focus().toggleStrike().run(),
          isActive: editor.isActive('strike'),
        },
      ],
    },
    {
      items: [
        {
          icon: <FaAlignLeft />,
          title: 'Alinhar à esquerda',
          action: () => editor.chain().focus().setTextAlign('left').run(),
          isActive: editor.isActive({ textAlign: 'left' }),
        },
        {
          icon: <FaAlignCenter />,
          title: 'Centralizar',
          action: () => editor.chain().focus().setTextAlign('center').run(),
          isActive: editor.isActive({ textAlign: 'center' }),
        },
        {
          icon: <FaAlignRight />,
          title: 'Alinhar à direita',
          action: () => editor.chain().focus().setTextAlign('right').run(),
          isActive: editor.isActive({ textAlign: 'right' }),
        },
        {
          icon: <FaAlignJustify />,
          title: 'Justificar',
          action: () => editor.chain().focus().setTextAlign('justify').run(),
          isActive: editor.isActive({ textAlign: 'justify' }),
        },
      ],
    },
    {
      items: [
        {
          icon: <FaListUl />,
          title: 'Lista com marcadores',
          action: () => editor.chain().focus().toggleBulletList().run(),
          isActive: editor.isActive('bulletList'),
        },
        {
          icon: <FaListOl />,
          title: 'Lista numerada',
          action: () => editor.chain().focus().toggleOrderedList().run(),
          isActive: editor.isActive('orderedList'),
        },
      ],
    },
    {
      items: [
        {
          icon: <FaImage />,
          title: 'Inserir imagem',
          action: () => {
            fileInputRef.current?.click();
            return true;
          },
        },
      ],
    },
  ];

  return (
    <div className={styles.toolbar} data-tour="toolbar">
      {/* Selects de formatação */}
      <div className={styles.group}>
        <select
          className={styles.select}
          value={getCurrentHeading()}
          onChange={handleHeadingChange}
          title="Estilo do parágrafo"
        >
          <option value="0">Normal</option>
          <option value="1">Título 1</option>
          <option value="2">Título 2</option>
          <option value="3">Título 3</option>
          <option value="4">Título 4</option>
        </select>
        <select
          className={styles.select}
          onChange={handleFontFamilyChange}
          title="Fonte"
          value={getCurrentFontFamily()}
        >
          {FONT_FAMILIES.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          className={styles.select}
          onChange={handleFontSizeChange}
          title="Tamanho da fonte"
          value={getCurrentFontSize()}
        >
          {FONT_SIZES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {groups.map((group, groupIndex) => (
        <div key={groupIndex} className={styles.group}>
          {group.items.map((item, itemIndex) => (
            <button
              key={itemIndex}
              className={`${styles.button} ${item.isActive ? styles.active : ''}`}
              onClick={item.action}
              disabled={item.disabled}
              title={item.title}
              type="button"
            >
              {item.icon}
            </button>
          ))}
        </div>
      ))}

      {/* Cor do texto e destaque */}
      <div className={styles.group}>
        <button
          className={styles.button}
          onClick={() => colorInputRef.current?.click()}
          title="Cor do texto"
          type="button"
        >
          <span style={{ fontWeight: 'bold', fontSize: '14px', borderBottom: '3px solid currentColor' }}>A</span>
        </button>
        <button
          className={styles.button}
          onClick={() => highlightInputRef.current?.click()}
          title="Destacar texto"
          type="button"
        >
          <FaHighlighter />
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImagePick}
      />
      <input
        ref={colorInputRef}
        type="color"
        style={{ display: 'none' }}
        onChange={handleColorChange}
        defaultValue="#000000"
      />
      <input
        ref={highlightInputRef}
        type="color"
        style={{ display: 'none' }}
        onChange={handleHighlightChange}
        defaultValue="#ffff00"
      />
    </div>
  );
}
